import { useState, useEffect, useCallback } from "react";
import { Waves, Plus, Trash2, Loader2, Settings2, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";

const COLOR = "#2B7BB9";
const MODES = ["Goutte-à-goutte", "Micro-aspersion", "Aspersion", "Enrouleur / canon", "Pivot / rampe", "Gravitaire (à la raie)", "Manuel (tuyau, arrosoir)"];
const SOURCES = ["Réseau collectif (périmètre irrigué)", "Forage / puits", "Retenue / bassin", "Rivière / ravine", "Eau de pluie récupérée", "Réseau d'eau potable"];
const UNITS = [{ v: "mm", l: "mm" }, { v: "m3/ha", l: "m³/ha" }, { v: "m3", l: "m³ (total)" }];
const unitLabel = (u) => (UNITS.find((x) => x.v === u) || {}).l || u;
const fmt = (n, d = 1) => Number(n).toLocaleString("fr-FR", { maximumFractionDigits: d });
const today = () => new Date().toISOString().slice(0, 10);

/** Dose en mm (1 mm = 10 m³/ha), pour cumuler des apports saisis dans des unités différentes. */
function toMm(value, unit, surfaceHa) {
  const v = Number(value) || 0;
  if (unit === "mm") return v;
  if (unit === "m3/ha") return v / 10;
  if (unit === "m3" && surfaceHa) return v / surfaceHa / 10;
  return null;
}

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="w-full h-9 rounded-md border border-input bg-white px-2 text-sm">
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function SettingsForm({ initial, onSave, onCancel, saving }) {
  const [mode, setMode] = useState(initial?.mode || "");
  const [source, setSource] = useState(initial?.water_source || "");
  const [dose, setDose] = useState(initial?.dose_value ?? "");
  const [unit, setUnit] = useState(initial?.dose_unit || "mm");
  const [duration, setDuration] = useState(initial?.duration_min ?? "");
  const submit = () => {
    if (!mode) { toast.error("Choisissez le mode d'irrigation"); return; }
    if (!(Number(dose) > 0)) { toast.error("Renseignez la dose moyenne"); return; }
    onSave({ mode, water_source: source || null, dose_value: Number(dose), dose_unit: unit, duration_min: duration === "" ? null : Number(duration) });
  };
  return (
    <div className="space-y-2.5" data-testid="irr-settings-form">
      <div>
        <Label className="text-[10px] uppercase">Mode d'irrigation *</Label>
        <Select value={mode} onChange={setMode} options={MODES} placeholder="Choisir…" testId="irr-mode" />
      </div>
      <div>
        <Label className="text-[10px] uppercase">Source d'eau</Label>
        <Select value={source} onChange={setSource} options={SOURCES} placeholder="Choisir… (facultatif)" testId="irr-source" />
      </div>
      <div className="grid grid-cols-[1fr_7rem] gap-2">
        <div>
          <Label className="text-[10px] uppercase">Dose moyenne par irrigation *</Label>
          <Input type="number" step="any" min="0" value={dose} onChange={(e) => setDose(e.target.value)} placeholder="ex. 25" className="h-9 text-sm" data-testid="irr-dose" />
        </div>
        <div>
          <Label className="text-[10px] uppercase">Unité</Label>
          <select value={unit} onChange={(e) => setUnit(e.target.value)} className="w-full h-9 rounded-md border border-input bg-white px-2 text-sm" data-testid="irr-unit">
            {UNITS.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
          </select>
        </div>
      </div>
      <div>
        <Label className="text-[10px] uppercase">Durée moyenne (minutes)</Label>
        <Input type="number" min="0" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="facultatif" className="h-9 text-sm" />
      </div>
      <p className="text-[10px] text-gray-500">Repère : 1 mm = 10 m³/ha.</p>
      <div className="flex gap-2 justify-end">
        {onCancel && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={onCancel}>Annuler</Button>}
        <Button size="sm" className="h-8 text-xs text-white" style={{ background: COLOR }} onClick={submit} disabled={saving} data-testid="irr-settings-save">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />} Enregistrer
        </Button>
      </div>
    </div>
  );
}

export default function IrrigationTab({ parcelId, surfaceHa, ilotId, ilots = [] }) {
  const ilotName = (id) => (ilots.find((i) => i.ilot_id === id) || {}).name;
  const q = `parcel_id=${encodeURIComponent(parcelId)}${ilotId ? `&ilot_id=${encodeURIComponent(ilotId)}` : ""}`;
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [custom, setCustom] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        authFetch(`${API_BASE}/carnet/irrigation/settings?${q}`).then((r) => (r.ok ? r.json() : {})),
        authFetch(`${API_BASE}/carnet/irrigation?${q}`).then((r) => (r.ok ? r.json() : [])),
      ]);
      setSettings(s && s.mode ? s : null);
      setItems(Array.isArray(l) ? l : []);
    } catch { /* l'écran reste vide */ }
    setLoading(false);
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async (body) => {
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/irrigation/settings`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ parcel_id: parcelId, ilot_id: ilotId || null, ...body }),
      });
      if (!res.ok) throw new Error();
      toast.success("Réglage de l'irrigation enregistré");
      setEditing(false); load();
    } catch { toast.error("Enregistrement impossible"); }
    setSaving(false);
  };

  const remove = async (id, silent) => {
    const res = await authFetch(`${API_BASE}/carnet/irrigation/${id}`, { method: "DELETE" });
    if (res.ok) { if (!silent) toast.success("Irrigation supprimée"); setDeletingId(null); load(); }
    else toast.error("Suppression impossible");
  };

  const add = async (entry) => {
    setAdding(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/irrigation`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parcel_id: parcelId, ilot_id: ilotId || null, mode: settings?.mode, water_source: settings?.water_source, ...entry }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error();
      toast.success(`Irrigation ajoutée : ${fmt(d.dose_value)} ${unitLabel(d.dose_unit)}`, {
        description: new Date(`${d.date}T00:00:00`).toLocaleDateString("fr-FR"),
        action: { label: "Annuler", onClick: () => remove(d.irrigation_id, true) },
      });
      setCustom(null); load();
    } catch { toast.error("Ajout impossible"); }
    setAdding(false);
  };

  const quickAdd = () => add({ date: today(), dose_value: settings.dose_value, dose_unit: settings.dose_unit, duration_min: settings.duration_min ?? null, quick: true });

  // Cumul de l'année en cours (mm, et m³ sur la surface de la parcelle ou de l'îlot)
  const year = String(new Date().getFullYear());
  const yearItems = items.filter((i) => (i.date || "").startsWith(year));
  const mmList = yearItems.map((i) => toMm(i.dose_value, i.dose_unit, surfaceHa));
  const totalMm = mmList.every((v) => v != null) ? mmList.reduce((a, b) => a + b, 0) : null;

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: COLOR }} /></div>;

  return (
    <ScrollArea className="h-full" data-testid="irrigation-tab">
      {/* Tout l'onglet défile : sur un petit écran, les formulaires du haut restent atteignables */}
      <div className="p-3 border-b border-black/5 space-y-2">
        {!settings || editing ? (
          <div className="rounded-xl border p-3" style={{ borderColor: `${COLOR}40`, background: `${COLOR}0A` }}>
            <p className="text-xs font-bold mb-2 flex items-center gap-1.5" style={{ color: COLOR }}>
              <Settings2 className="w-3.5 h-3.5" /> {settings ? "Modifier mon irrigation" : "Mon irrigation : à renseigner une fois"}
            </p>
            <SettingsForm initial={settings} saving={saving} onSave={saveSettings} onCancel={settings ? () => setEditing(false) : null} />
          </div>
        ) : (
          <>
            <div className="rounded-xl border px-3 py-2 flex items-center gap-2" style={{ borderColor: `${COLOR}30`, background: `${COLOR}08` }} data-testid="irr-settings-summary">
              <Waves className="w-4 h-4 shrink-0" style={{ color: COLOR }} />
              <div className="flex-1 min-w-0 text-[11px] leading-snug">
                <span className="font-semibold text-gray-900">{settings.mode}</span>
                {settings.water_source && <span className="text-gray-600"> · {settings.water_source}</span>}
                <span className="text-gray-600"> · {fmt(settings.dose_value)} {unitLabel(settings.dose_unit)} en moyenne{settings.duration_min ? ` · ${settings.duration_min} min` : ""}</span>
              </div>
              <button onClick={() => setEditing(true)} className="text-[11px] underline text-gray-600 shrink-0" data-testid="irr-settings-edit">Modifier</button>
            </div>
            <Button className="w-full h-11 text-sm font-semibold rounded-xl text-white gap-2" style={{ background: COLOR }}
              onClick={quickAdd} disabled={adding} data-testid="irr-quick-add">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Irrigation du jour · {fmt(settings.dose_value)} {unitLabel(settings.dose_unit)}
            </Button>
            {custom ? (
              <div className="rounded-xl border border-black/10 p-3 space-y-2" data-testid="irr-custom-form">
                <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_7rem] gap-2">
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="text-[10px] uppercase">Date</Label>
                    <Input type="date" value={custom.date} onChange={(e) => setCustom({ ...custom, date: e.target.value })} className="h-9 text-sm" data-testid="irr-custom-date" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase">Dose</Label>
                    <Input type="number" step="any" min="0" value={custom.dose_value} onChange={(e) => setCustom({ ...custom, dose_value: e.target.value })} className="h-9 text-sm" data-testid="irr-custom-dose" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase">Unité</Label>
                    <select value={custom.dose_unit} onChange={(e) => setCustom({ ...custom, dose_unit: e.target.value })} className="w-full h-9 rounded-md border border-input bg-white px-2 text-sm">
                      {UNITS.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
                    </select>
                  </div>
                </div>
                <Input value={custom.notes} onChange={(e) => setCustom({ ...custom, notes: e.target.value })} placeholder="Note (facultatif)" className="h-9 text-sm" />
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setCustom(null)}>Annuler</Button>
                  <Button size="sm" className="h-8 text-xs text-white" style={{ background: COLOR }} disabled={adding || !(Number(custom.dose_value) > 0) || !custom.date}
                    onClick={() => add({ date: custom.date, dose_value: Number(custom.dose_value), dose_unit: custom.dose_unit, notes: custom.notes || null })} data-testid="irr-custom-save">
                    Ajouter
                  </Button>
                </div>
              </div>
            ) : (
              <button className="w-full text-[11px] text-gray-600 underline flex items-center justify-center gap-1"
                onClick={() => setCustom({ date: today(), dose_value: settings.dose_value, dose_unit: settings.dose_unit, notes: "" })} data-testid="irr-custom-open">
                <CalendarDays className="w-3 h-3" /> Autre date ou autre dose
              </button>
            )}
          </>
        )}
      </div>

      <div>
        <div className="p-3 space-y-2">
          {yearItems.length > 0 && (
            <div className="flex gap-2 flex-wrap" data-testid="irr-total">
              <Badge className="text-[10px] font-semibold border-0 py-0.5 px-2" style={{ background: `${COLOR}14`, color: COLOR }}>
                {yearItems.length} irrigation{yearItems.length > 1 ? "s" : ""} en {year}
              </Badge>
              {totalMm != null && (
                <Badge className="text-[10px] font-semibold border-0 py-0.5 px-2" style={{ background: `${COLOR}14`, color: COLOR }}>
                  Cumul {fmt(totalMm)} mm{surfaceHa ? ` · ${fmt(totalMm * 10 * surfaceHa, 0)} m³ sur ${fmt(surfaceHa, 2)} ha` : ""}
                </Badge>
              )}
            </div>
          )}
          {items.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Waves className="w-8 h-8 mx-auto mb-3 opacity-20" />
              <p className="text-xs" style={{ color: "#7A7A7A" }}>Aucune irrigation enregistrée</p>
            </div>
          ) : items.map((it) => (
            <div key={it.irrigation_id} className="rounded-xl border border-black/5 p-3 flex items-start gap-3" data-testid={`irr-${it.irrigation_id}`}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${COLOR}14` }}>
                <Waves className="w-4 h-4" style={{ color: COLOR }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-semibold text-gray-900">{fmt(it.dose_value)} {unitLabel(it.dose_unit)}</span>
                  <span className="text-[10px] text-gray-400">{new Date(`${it.date}T00:00:00`).toLocaleDateString("fr-FR")}</span>
                  {it.ilot_id && ilotName(it.ilot_id) && <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: "#dbe8f1", color: "#2f6a92" }}>{ilotName(it.ilot_id)}</Badge>}
                </div>
                <p className="text-[10px] text-gray-500">{[it.mode, it.water_source, it.duration_min ? `${it.duration_min} min` : null].filter(Boolean).join(" · ")}</p>
                {it.notes && <p className="text-[10px] italic text-gray-500 mt-0.5">{it.notes}</p>}
              </div>
              {deletingId === it.irrigation_id ? (
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="h-6 text-[9px] px-2 rounded-lg border-red-200 text-red-600" onClick={() => remove(it.irrigation_id)}>Supprimer</Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[9px] px-1" onClick={() => setDeletingId(null)}>Non</Button>
                </div>
              ) : (
                <button onClick={() => setDeletingId(it.irrigation_id)} className="p-1 rounded hover:bg-red-50" aria-label="Supprimer"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
              )}
            </div>
          ))}
        </div>
      </div>
    </ScrollArea>
  );
}
